import { useMemo } from "react";

export default function ExplainThisPrice({
  car,
  askingPrice,
  mestimate,
  comps = [],
  loading = false,
  error = false,
  unavailableMessage = "Comparable sales are still too thin to estimate value.",
}) {
  const insight = useMemo(() => {
    const asking = Number(askingPrice);
    const estimate = Number(mestimate);

    if (!Number.isFinite(asking) || !Number.isFinite(estimate) || estimate <= 0) {
      return null;
    }

    const delta = asking - estimate;
    const pct = delta / estimate;
    let verdict = "Fair";
    if (pct <= -0.05) verdict = "Great deal";
    else if (pct >= 0.12) verdict = "Overpriced";
    else if (pct >= 0.05) verdict = "High";

    const compMiles = comps.map((c) => Number(c?.mileage)).filter(Number.isFinite);
    const compDates = comps
      .map((c) => (c?.soldDate ? daysSince(c.soldDate) : null))
      .filter((v) => typeof v === "number");

    const avgMiles = compMiles.length ? Math.round(avg(compMiles)) : null;
    const compsCount = comps.length;
    const freshCount = compDates.filter((d) => d <= 60).length;
    const confidence = computeConfidence(compsCount, freshCount);

    const reasons = [];
    if (car?.mileage && avgMiles) {
      const mileDelta = car.mileage - avgMiles;
      if (mileDelta <= -7000) reasons.push("Lower mileage than recent sold comps.");
      else if (mileDelta >= 7000) reasons.push("Higher mileage than recent sold comps.");
    }
    if (compsCount > 0 && compsCount < 3) {
      reasons.push("Limited comp sample size.");
    }
    if (freshCount >= Math.ceil(Math.max(compsCount, 1) / 2)) {
      reasons.push("Most comps are recent.");
    }

    const summary =
      delta === 0
        ? "Asking price matches the Meshum Estimate."
        : delta > 0
          ? `Asking price is ${currency(Math.abs(delta))} above the Meshum Estimate.`
          : `Asking price is ${currency(Math.abs(delta))} below the Meshum Estimate.`;

    return {
      verdict,
      delta,
      pct,
      summary,
      reasons,
      tags: [verdict, compsCount ? `${compsCount} sold comps` : "No sold comps", confidence.level].filter(Boolean),
      stats: {
        compsCount,
        avgMiles,
      },
      confidence,
      negotiation: buildNegotiation({ asking, estimate, verdict, delta }),
    };
  }, [askingPrice, mestimate, comps, car]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">Market insight</span>
          <StatusBadge tone={loading || error ? "neutral" : toneForVerdict(insight?.verdict)}>
            {loading ? "Loading" : error ? "Unavailable" : insight?.verdict || "Unavailable"}
          </StatusBadge>
          <span className="text-sm text-slate-600">
            {loading
              ? "Analyzing sold comps..."
              : error
                  ? "Estimate data could not be loaded right now."
                  : insight
                    ? insight.summary
                    : unavailableMessage
            }
          </span>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {loading ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Loading estimate and comparable sales...
          </div>
        ) : error ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Estimate unavailable. Try refreshing the page.
          </div>
        ) : !insight ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {unavailableMessage}
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <ValueCard label="Asking price" value={currency(askingPrice)} />
              <ValueCard label="Meshum Estimate" value={currency(mestimate)} accent />
              <ValueCard
                label="Difference"
                value={`${insight.delta > 0 ? "+" : ""}${currency(insight.delta)}`}
                tone={insight.delta > 0 ? "bad" : insight.delta < 0 ? "good" : "neutral"}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Confidence</span>
                <ConfidenceBar score={insight.confidence.score} level={insight.confidence.level} />
              </div>
              <div className="mt-2 text-sm text-slate-600">
                {insight.stats.compsCount > 0
                  ? `Built from ${insight.stats.compsCount} recent sold vehicles${typeof insight.stats.avgMiles === "number" ? ` with an average of ${formatInt(insight.stats.avgMiles)} miles` : ""}.`
                  : "Market context is available, but there are not enough sold comps to show a confident estimate."}
              </div>
            </div>

            {insight.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {insight.tags.map((tag) => (
                  <Chip key={tag} label={tag} />
                ))}
              </div>
            )}

            {insight.reasons.length > 0 && (
              <ul className="space-y-2 text-sm text-slate-700">
                {insight.reasons.map((reason) => (
                  <li key={reason} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                    {reason}
                  </li>
                ))}
              </ul>
            )}

            {insight.negotiation && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                <span className="font-medium">Negotiation tip: </span>
                {insight.negotiation.text}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
              Based on recent comparable sales and the current asking price. Extreme outliers are excluded.
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function ValueCard({ label, value, accent = false, tone = "neutral" }) {
  const toneStyles = {
    neutral: "text-slate-900",
    good: "text-emerald-700",
    bad: "text-rose-700",
  };

  return (
    <div className={`rounded-xl border px-4 py-3 ${accent ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${toneStyles[tone] || toneStyles.neutral}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    fair: "bg-amber-50 text-amber-700 border-amber-200",
    bad: "bg-rose-50 text-rose-700 border-rose-200",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function Chip({ label }) {
  return <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">{label}</span>;
}

function ConfidenceBar({ score, level }) {
  const pct = Math.max(0, Math.min(100, Math.round(score)));
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-28 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-2 rounded-full ${pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-slate-700">{level}</span>
    </div>
  );
}

function computeConfidence(compsCount, freshCount) {
  let score = (Math.min(10, Math.max(0, compsCount)) / 10) * 60;
  score += Math.min(30, freshCount * 10);
  score = Math.max(0, Math.min(100, Math.round(score)));
  const level = score >= 70 ? "High" : score >= 40 ? "Medium" : "Low";
  return { score, level };
}

function buildNegotiation({ asking, estimate, verdict, delta }) {
  const roundTo = (n, step = 100) => Math.round(n / step) * step;
  const below = (n, minBelow = 100) => Math.min(asking - minBelow, n);

  if (verdict === "Great deal") {
    const offer = below(roundTo(Math.max(asking * 0.99, estimate * 0.98)));
    return { text: `Priced below similar cars. If you want it, consider offering around ${currency(offer)}.` };
  }

  if (verdict === "Fair") {
    const start = below(roundTo(Math.min(asking, estimate)));
    const likely = below(roundTo((asking + estimate) / 2));
    return { text: `Near market value. Start around ${currency(start)} and work toward ${currency(likely)} if the car checks out.` };
  }

  if (verdict === "High") {
    const start = below(roundTo(estimate));
    const target = below(roundTo(asking - Math.min(Math.abs(delta) * 0.4, 1500)));
    return { text: `Above market. Start near ${currency(start)} and aim closer to ${currency(target)}.` };
  }

  const start = below(roundTo(Math.max(0, estimate * 0.98)));
  const target = below(roundTo(Math.max(start, asking - Math.min(Math.abs(delta) * 0.5, 2500))));
  return { text: `Well above market. Open near ${currency(start)} and be ready to walk if the price will not approach ${currency(target)}.` };
}

function toneForVerdict(v) {
  if (v === "Great deal") return "good";
  if (v === "Fair") return "fair";
  if (v === "High" || v === "Overpriced") return "bad";
  return "neutral";
}

function currency(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "-";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatInt(n) {
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function avg(xs) {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function daysSince(dateLike) {
  const d = new Date(dateLike);
  if (isNaN(d)) return null;
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}
